import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, UseFormReturn } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Container, Col, Row, Stack, Form, Card,
} from 'react-bootstrap';

import SVButton from 'components/elements/SVButton';
import BurnBedsGrid, { getBurnBedsStaffingValidation } from 'components/forms/BurnBedsGrid';
import RehabBedsGrid from 'components/forms/RehabBedsGrid';
import api from 'api';
import { SectionTitle, SectionWrapperBox } from 'components/layout/SectionContainer';
import useFeedback from 'hooks/useFeedback';
import { BurncenterAccessLevelEnum } from 'utils/enums';
import { BenchmarkType } from 'types/burnCenter';

const nullableNumber = () => yup
  .number()
  .transform((value, originalValue) => (originalValue === '' ? null : value))
  .nullable();

export interface BurnCenterFormValues {
  name: string;
  hospitalName: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressLine3?: string | null;
  city?: string | null;
  location?: string | null;
  country?: string | null;
  zipPostalCode?: string | null;
  region?: string | null;
  sigmaTeam?: string | null;
  jwtSigmaWorkspaceLandingLink?: string | null;
  burnCenterAccessLevel?: string | null;
  phoneNumber?: string | null;
  faxNumber?: string | null;
  tollFreeNumber?: string | null;
  email?: string | null;
  website?: string | null;
  benchmark?: BenchmarkType;
}

interface FormInputProps {
  name: keyof BurnCenterFormValues | `benchmark.${keyof BenchmarkType}`;
  label?: string;
  required?: boolean;
  form: UseFormReturn<BurnCenterFormValues>;
  [key: string]: any;
}

const requiredTag = (required?: boolean) => (
  <span
    style={{
      marginLeft: '5px',
      fontSize: '1.5rem',
      color: required ? 'red' : 'transparent',
    }}
    aria-hidden={!required}
  >
    *
  </span>
);

const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  required,
  form: {
    register,
    formState: { errors },
  },
  ...inputProps
}) => {
  const error = errors && (errors as any)[name];
  const isCheckbox = inputProps.type === 'checkbox';

  return (
    <Form.Group className="mb-3" controlId={name}>
      {isCheckbox ? (
        <Form.Check
          {...register(name)}
          type="checkbox"
          label={label}
          isInvalid={!!error}
        />
      ) : (
        <>
          {label && (
            <Form.Label className="fw-bold">
              {label}
              {requiredTag(required)}
            </Form.Label>
          )}
          <Form.Control
            type="text"
            {...register(name)}
            isInvalid={!!error}
            {...inputProps}
          />
        </>
      )}
      <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
        {error?.message as string}
      </Form.Control.Feedback>
    </Form.Group>
  );
};

const schema = yup.object({
  name: yup.string().required('Burn Center Name is required'),
  hospitalName: yup.string().required('Hospital Name is required'),
  addressLine1: yup.string().nullable(),
  addressLine2: yup.string().nullable(),
  addressLine3: yup.string().nullable(),
  city: yup.string().nullable(),
  location: yup.string().nullable(),
  country: yup.string().nullable(),
  zipPostalCode: yup.string().nullable(),
  region: yup.string().nullable(),
  sigmaTeam: yup.string().nullable(),
  jwtSigmaWorkspaceLandingLink: yup.string().nullable(),
  burnCenterAccessLevel: yup
    .string()
    .oneOf(Object.values(BurncenterAccessLevelEnum))
    .nullable(),
  phoneNumber: yup.string().nullable(),
  faxNumber: yup.string().nullable(),
  tollFreeNumber: yup.string().nullable(),
  email: yup.string().email().matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email').nullable(),
  website: yup.string().url().nullable(),
  benchmark: yup.object().shape({
    averageDailyHospitalOccupancy: nullableNumber(),
    hospitalTotalStaffedBeds: nullableNumber(),
    hospitalTotalStaffedIcuBeds: nullableNumber(),
    burnLicensedAdultBeds: nullableNumber(),
    burnLicensedPediatricBeds: nullableNumber(),
    burnLicensedTotalBeds: nullableNumber(),
    burnStaffedIcuAdultBeds: nullableNumber(),
    burnStaffedIcuPediatricBeds: nullableNumber(),
    burnStaffedIcuTotalBeds: nullableNumber(),
    burnStaffedNonIcuAdultBeds: nullableNumber(),
    burnStaffedNonIcuPediatricBeds: nullableNumber(),
    burnStaffedNonIcuTotalBeds: nullableNumber(),
    burnStaffedTotalAdultBeds: nullableNumber(),
    burnStaffedTotalPediatricBeds: nullableNumber(),
    burnStaffedTotalBeds: nullableNumber(),
    rehabLicensedInpatientAdultBeds: nullableNumber(),
    rehabLicensedInpatientPediatricBeds: nullableNumber(),
    rehabLicensedInpatientTotalBeds: nullableNumber(),
    pediatricPatientDefinition: yup.string().nullable(),
  }).test('burn-beds-staffing', function validateBurnBedsStaffing(benchmark) {
    const result = getBurnBedsStaffingValidation(benchmark ?? {});
    if (!result.isValid) {
      return this.createError({
        message: result.messages.join(' '),
      });
    }
    return true;
  }),
}) as yup.ObjectSchema<BurnCenterFormValues>;

const BurnCenterAdd: React.FC = () => {
  const [burnBedsStaffingValid, setBurnBedsStaffingValid] = React.useState(true);
  const navigate = useNavigate();
  const { setError } = useFeedback?.() ?? {
    setError: () => {},
  };
  const formProps = useForm<BurnCenterFormValues>({
    defaultValues: {
      name: '',
      hospitalName: '',
      benchmark: {
        averageDailyHospitalOccupancy: null,
        hospitalTotalStaffedBeds: null,
        hospitalTotalStaffedIcuBeds: null,
        burnLicensedAdultBeds: null,
        burnLicensedPediatricBeds: null,
        burnLicensedTotalBeds: null,
        burnStaffedIcuAdultBeds: null,
        burnStaffedIcuPediatricBeds: null,
        burnStaffedIcuTotalBeds: null,
        burnStaffedNonIcuAdultBeds: null,
        burnStaffedNonIcuPediatricBeds: null,
        burnStaffedNonIcuTotalBeds: null,
        burnStaffedTotalAdultBeds: null,
        burnStaffedTotalPediatricBeds: null,
        burnStaffedTotalBeds: null,
        rehabLicensedInpatientAdultBeds: null,
        rehabLicensedInpatientPediatricBeds: null,
        rehabLicensedInpatientTotalBeds: null,
        pediatricPatientDefinition: null,
      },
      addressLine1: null,
      addressLine2: null,
      addressLine3: null,
      city: null,
      country: null,
      location: null,
      zipPostalCode: null,
      region: null,
      sigmaTeam: null,
      jwtSigmaWorkspaceLandingLink: null,
      burnCenterAccessLevel: null,
      phoneNumber: null,
      faxNumber: null,
      tollFreeNumber: null,
      email: null,
      website: null,
    },
    resolver: yupResolver(schema),
  });

  const { handleSubmit } = formProps;

  const onSubmit = async (data: BurnCenterFormValues) => {
    try {
      await api.addBurnCenter(data);
      navigate('..');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError?.(detail || 'Failed to create burn center.');
    }
  };

  return (
    <Container className="site-content">
      <SectionWrapperBox>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex justify-content-between align-items-center mb-3 mt-10">
            <SectionTitle>New Burn Center</SectionTitle>
          </div>
          <Card className="mt-4 border shadow-sm">
            <Card.Header className="bg-light fw-bold fs-5">About</Card.Header>
            <Card.Body>
              <Row className="g-3 align-items-start mb-0">
                <Col xs={12} md={4}>
                  <FormInput
                    label="Burn Center Name"
                    name="name"
                    required
                    form={formProps}
                  />
                </Col>
                <Col xs={12} md={4}>
                  <FormInput
                    label="Hospital Name"
                    name="hospitalName"
                    required
                    form={formProps}
                  />
                </Col>
              </Row>
              <Row className="g-3 align-items-start">
                <Col xs={12} md={4}>
                  <FormInput
                    label="Address one"
                    name="addressLine1"
                    form={formProps}
                    autoComplete="address-line1"
                  />
                  <FormInput
                    label="Address two"
                    name="addressLine2"
                    form={formProps}
                    autoComplete="address-line2"
                  />
                  <FormInput
                    label="Address three"
                    name="addressLine3"
                    form={formProps}
                    autoComplete="address-line3"
                  />
                  <FormInput label="City" name="city" form={formProps} autoComplete="address-level2" />
                  <FormInput
                    label="Region"
                    name="region"
                    form={formProps}
                    autoComplete="address-level1"
                  />
                  <FormInput
                    label="Postal Code"
                    name="zipPostalCode"
                    form={formProps}
                    autoComplete="postal-code"
                  />
                  <FormInput
                    label="State or Province"
                    name="location"
                    form={formProps}
                    autoComplete="location"
                  />
                  <FormInput
                    label="Country"
                    name="country"
                    form={formProps}
                    autoComplete="country"
                  />
                </Col>
                <Col xs={12} md={4}>
                  <FormInput
                    label="Phone number"
                    name="phoneNumber"
                    form={formProps}
                    autoComplete="tel"
                  />
                  <FormInput
                    label="Toll free number"
                    name="tollFreeNumber"
                    form={formProps}
                  />
                  <FormInput
                    label="Fax number"
                    name="faxNumber"
                    form={formProps}
                  />
                </Col>
                <Col xs={12} md={4}>
                  <FormInput
                    label="Website"
                    name="website"
                    form={formProps}
                    autoComplete="url"
                  />
                  <FormInput
                    label="Email"
                    name="email"
                    type="email"
                    form={formProps}
                    autoComplete="email"
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <Card className="mt-4 border shadow-sm">
            <Card.Body>
              <h5 className="fw-bold mb-3">Hospital Beds</h5>
              <Row className="g-3 align-items-start">
                <Col xs={12} md={6} lg={6}>
                  <FormInput
                    label="Hospital Total Beds (staffed)"
                    name="benchmark.hospitalTotalStaffedBeds"
                    type="number"
                    form={formProps}
                  />
                </Col>
                <Col xs={12} md={6} lg={6}>
                  <FormInput
                    label="Hospital Total ICU Beds (staffed)"
                    name="benchmark.hospitalTotalStaffedIcuBeds"
                    type="number"
                    form={formProps}
                  />
                </Col>
                <Col xs={12} md={6} lg={6}>
                  <FormInput
                    label="Average Daily Hospital Occupancy (%)"
                    name="benchmark.averageDailyHospitalOccupancy"
                    type="number"
                    form={formProps}
                  />
                </Col>

              </Row>
            </Card.Body>
          </Card>
          <Card className="mt-4 border shadow-sm">
            <Card.Body>
              <Col xs={12}>
                <BurnBedsGrid
                  form={formProps}
                  namePrefix="benchmark."
                  editing
                  disabled={false}
                  onStaffingValidationChange={setBurnBedsStaffingValid}
                />
              </Col>

            </Card.Body>
          </Card>
          <Card className="mt-4 border shadow-sm">
            <Card.Body>
              <Col xs={12}>
                <RehabBedsGrid
                  form={formProps}
                  namePrefix="benchmark."
                  editing
                  disabled={false}
                />
              </Col>
            </Card.Body>
          </Card>
          <Card className="mt-4 border shadow-sm mb-4">
            <Card.Header className="bg-light fw-bold fs-5">Setup</Card.Header>
            <Card.Body>
              <Row className="g-3 align-items-start">
                <Col xs={12} md={6} lg={4}>
                  <FormInput
                    label="Sigma Team"
                    name="sigmaTeam"
                    form={formProps}
                  />
                </Col>
                <Col xs={12} md={6} lg={4}>
                  <FormInput
                    label="JWT Sigma Workspace Link"
                    name="jwtSigmaWorkspaceLandingLink"
                    form={formProps}
                  />
                </Col>
                <Col xs={12} md={6} lg={4}>
                  <Form.Group
                    className="mb-3"
                    controlId="burnCenterAccessLevel"
                  >
                    <Form.Label className="fw-bold">
                      Access Level
                      {requiredTag()}
                    </Form.Label>
                    <Form.Select
                      {...formProps.register('burnCenterAccessLevel')}
                      isInvalid={
                        !!formProps.formState.errors.burnCenterAccessLevel
                      }
                      defaultValue=""
                    >
                      <option value="">Select access level</option>
                      {Object.values(BurncenterAccessLevelEnum).map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback
                      type="invalid"
                      style={{ display: 'block' }}
                    >
                      {
                        formProps.formState.errors.burnCenterAccessLevel
                          ?.message as string
                      }
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <div className="d-flex justify-content-end">
            <Stack direction="horizontal" gap={2}>
              <Link to=".." style={{ textDecoration: 'none' }}>
                <SVButton variant="cancel">Cancel</SVButton>
              </Link>
              <SVButton
                className="me-2"
                variant="secondary"
                type="submit"
                disabled={!burnBedsStaffingValid}
              >
                Save
              </SVButton>
            </Stack>
          </div>
        </Form>
      </SectionWrapperBox>
    </Container>
  );
};

export default BurnCenterAdd;
